"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserManagementTable = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * RedPanda UI - UserManagementTable（管理員面板：已核准／待審核使用者）
 * docs/介面優化.md R26：取代各系統幾乎相同的 components/UserManagementTable.tsx（CRM 已換用，其他系統依第十三節表格逐一換用）；
 * 各系統只傳入自己的 API 函數（都呼叫 identity-service）與目前使用者
 * 文字用共用翻譯 common.adminPanel.*、common.table.*、common.roles.*
 */
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const Modal_1 = require("../Modal");
const Table_1 = require("../Table");
const Input_1 = require("../Input");
const Loading_1 = require("../Loading");
const RowAction_1 = require("../RowAction");
const roleKey_1 = require("./roleKey");
const SUPER_ADMIN = 'SUPER_ADMIN';
const ADMIN = 'ADMIN';
const USER = 'USER';
/** 快取依目前使用者分開存：同一台電腦換帳號登入時，不會先看到上一位管理員組織的使用者 */
const cacheKey = (kind, userId) => `adminPanel_${kind}Users_${userId ?? 'anonymous'}`;
const readCache = (key) => {
    try {
        const cached = localStorage.getItem(key);
        return cached ? JSON.parse(cached) : [];
    }
    catch {
        return [];
    }
};
const writeCache = (key, users) => {
    try {
        localStorage.setItem(key, JSON.stringify(users));
    }
    catch { /* localStorage 不可用時略過 */ }
};
const errorMessage = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const UserManagementTable = ({ api, currentUser, formatDate }) => {
    const { t, i18n } = (0, react_i18next_1.useTranslation)();
    const currentUserId = currentUser?.id;
    const isSuperAdmin = currentUser?.role === SUPER_ADMIN;
    const approvedKey = cacheKey('approved', currentUserId);
    const pendingKey = cacheKey('pending', currentUserId);
    // 先用上次的快取顯示，避免重新整理時表格先空白再跳出來
    const [approvedUsers, setApprovedUsers] = (0, react_1.useState)(() => readCache(approvedKey));
    const [pendingUsers, setPendingUsers] = (0, react_1.useState)(() => readCache(pendingKey));
    const [loadError, setLoadError] = (0, react_1.useState)(null);
    const [actionError, setActionError] = (0, react_1.useState)(null);
    const [userToDeleteId, setUserToDeleteId] = (0, react_1.useState)(null);
    const [isDeleting, setIsDeleting] = (0, react_1.useState)(false);
    const [approvingUsers, setApprovingUsers] = (0, react_1.useState)(new Set());
    const [rejectingUsers, setRejectingUsers] = (0, react_1.useState)(new Set());
    const showDate = (value) => formatDate ? formatDate(value, i18n.language) : new Date(value).toLocaleDateString();
    // 狀態變動就同步快取（各操作只管更新狀態）
    (0, react_1.useEffect)(() => writeCache(approvedKey, approvedUsers), [approvedKey, approvedUsers]);
    (0, react_1.useEffect)(() => writeCache(pendingKey, pendingUsers), [pendingKey, pendingUsers]);
    (0, react_1.useEffect)(() => {
        let cancelled = false;
        setLoadError(null);
        Promise.all([api.getApprovedUsers(), api.getPendingUsers()])
            .then(([approvedResponse, pendingResponse]) => {
            if (cancelled)
                return;
            setApprovedUsers(approvedResponse.data.users);
            setPendingUsers(pendingResponse.data);
        })
            .catch((err) => {
            if (!cancelled)
                setLoadError(errorMessage(err, 'An unknown error occurred'));
        });
        return () => {
            cancelled = true;
        };
    }, [api]);
    const toggleBusy = (setter, id, busy) => setter(prev => {
        const next = new Set(prev);
        if (busy)
            next.add(id);
        else
            next.delete(id);
        return next;
    });
    // 以下操作一律用函式型 setState：同時進行多筆核准／拒絕時，每筆都以最新清單計算，不會互相覆蓋
    const handleRoleChange = async (id, newRole) => {
        setActionError(null);
        try {
            await api.updateUserRole(id, newRole);
            setApprovedUsers(prev => prev.map(user => (user.id === id ? { ...user, role: newRole } : user)));
        }
        catch (err) {
            setActionError(errorMessage(err, 'Failed to update user role'));
        }
    };
    const handleDeleteUser = (id) => {
        if (id === currentUserId) {
            setActionError(t('common.messages.cannotDeleteSelf'));
            return;
        }
        setUserToDeleteId(id);
    };
    const confirmDeleteUser = async () => {
        if (!userToDeleteId)
            return;
        const id = userToDeleteId;
        setActionError(null);
        setIsDeleting(true);
        try {
            await api.deleteUser(id);
            setApprovedUsers(prev => prev.filter(user => user.id !== id));
        }
        catch (err) {
            setActionError(errorMessage(err, 'Failed to delete user'));
        }
        finally {
            setUserToDeleteId(null);
            setIsDeleting(false);
        }
    };
    const handleApproveUser = async (id) => {
        const approvedUser = pendingUsers.find(user => user.id === id);
        setActionError(null);
        toggleBusy(setApprovingUsers, id, true);
        try {
            // 審核通過一律給 USER 角色與 ALL 權限（可存取所有業務系統）
            await api.approveUser(id, USER, { allowedSystems: ['ALL'] });
            setPendingUsers(prev => prev.filter(user => user.id !== id));
            if (approvedUser) {
                setApprovedUsers(prev => [...prev, { ...approvedUser, status: 'APPROVED', role: USER, allowedSystems: ['ALL'] }]);
            }
        }
        catch (err) {
            setActionError(errorMessage(err, 'Failed to approve user'));
        }
        finally {
            toggleBusy(setApprovingUsers, id, false);
        }
    };
    const handleRejectUser = async (id) => {
        setActionError(null);
        toggleBusy(setRejectingUsers, id, true);
        try {
            await api.rejectUser(id);
            setPendingUsers(prev => prev.filter(user => user.id !== id));
        }
        catch (err) {
            setActionError(errorMessage(err, 'Failed to reject user'));
        }
        finally {
            toggleBusy(setRejectingUsers, id, false);
        }
    };
    // 載入失敗才整塊換成錯誤訊息；單筆操作失敗只在表格上方提示，表格照常顯示
    if (loadError) {
        return (0, jsx_runtime_1.jsx)("p", { className: "py-6 text-center text-sm text-red-600 dark:text-red-400", children: loadError });
    }
    const empty = (0, jsx_runtime_1.jsx)("span", { className: "text-fg-muted", children: "\u2014" });
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)("div", { className: "space-y-8", children: [actionError && ((0, jsx_runtime_1.jsx)("p", { role: "alert", className: "rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300", children: actionError })), (0, jsx_runtime_1.jsxs)("div", { className: "bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg", children: [(0, jsx_runtime_1.jsx)("h3", { className: "px-6 py-3 text-sm font-semibold text-fg border-b border-line", children: t('common.adminPanel.approvedUsers') }), (0, jsx_runtime_1.jsxs)(Table_1.Table, { style: { tableLayout: 'fixed', minWidth: '640px' }, children: [(0, jsx_runtime_1.jsx)(Table_1.TableHeader, { children: (0, jsx_runtime_1.jsxs)(Table_1.TableRow, { children: [(0, jsx_runtime_1.jsx)(Table_1.TableHead, { children: t('common.table.columns.email') }), (0, jsx_runtime_1.jsx)(Table_1.TableHead, { className: "w-40", children: t('common.table.columns.name') }), (0, jsx_runtime_1.jsx)(Table_1.TableHead, { className: "w-44", children: t('common.table.columns.role') }), (0, jsx_runtime_1.jsx)(Table_1.TableHead, { className: "w-32", children: t('common.table.columns.createdAt') }), (0, jsx_runtime_1.jsx)(Table_1.TableHead, { className: "w-28", children: t('common.table.columns.actions') })] }) }), (0, jsx_runtime_1.jsx)(Table_1.TableBody, { children: approvedUsers.length === 0 ? ((0, jsx_runtime_1.jsx)(Table_1.TableRow, { children: (0, jsx_runtime_1.jsx)(Table_1.TableCell, { colSpan: 5, className: "text-center py-8 !text-fg-secondary", children: t('common.adminPanel.noApprovedUsers') }) })) : (approvedUsers.map(user => ((0, jsx_runtime_1.jsxs)(Table_1.TableRow, { children: [(0, jsx_runtime_1.jsx)(Table_1.TableCell, { className: "font-medium truncate", children: user.email }), (0, jsx_runtime_1.jsx)(Table_1.TableCell, { className: "truncate", children: user.name || empty }), (0, jsx_runtime_1.jsx)(Table_1.TableCell, { children: isSuperAdmin && user.role !== SUPER_ADMIN ? ((0, jsx_runtime_1.jsx)(Input_1.CustomSelect, { value: user.role, onChange: (value) => handleRoleChange(user.id, value), options: [
                                                            { value: USER, label: t('common.roles.user') },
                                                            { value: ADMIN, label: t('common.roles.admin') },
                                                        ] })) : ((0, jsx_runtime_1.jsx)("span", { children: t(`common.roles.${(0, roleKey_1.roleKey)(user.role)}`) })) }), (0, jsx_runtime_1.jsx)(Table_1.TableCell, { className: "truncate tabular-nums", children: showDate(user.createdAt) }), (0, jsx_runtime_1.jsx)(Table_1.TableCell, { children: isSuperAdmin && user.role !== SUPER_ADMIN && user.id !== currentUserId && ((0, jsx_runtime_1.jsx)(RowAction_1.RowAction, { tone: "danger", onClick: () => handleDeleteUser(user.id), children: t('common.actions.delete') })) })] }, user.id)))) })] })] }), pendingUsers.length > 0 && ((0, jsx_runtime_1.jsxs)("div", { className: "bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg", children: [(0, jsx_runtime_1.jsx)("h3", { className: "px-6 py-3 text-sm font-semibold text-fg border-b border-line", children: t('common.adminPanel.pendingUsers') }), (0, jsx_runtime_1.jsxs)(Table_1.Table, { style: { tableLayout: 'fixed', minWidth: '560px' }, children: [(0, jsx_runtime_1.jsx)(Table_1.TableHeader, { children: (0, jsx_runtime_1.jsxs)(Table_1.TableRow, { children: [(0, jsx_runtime_1.jsx)(Table_1.TableHead, { children: t('common.table.columns.email') }), (0, jsx_runtime_1.jsx)(Table_1.TableHead, { className: "w-40", children: t('common.table.columns.name') }), (0, jsx_runtime_1.jsx)(Table_1.TableHead, { className: "w-32", children: t('common.table.columns.createdAt') }), (0, jsx_runtime_1.jsx)(Table_1.TableHead, { className: "w-44", children: t('common.table.columns.actions') })] }) }), (0, jsx_runtime_1.jsx)(Table_1.TableBody, { children: pendingUsers.map(user => {
                                            const busy = approvingUsers.has(user.id) || rejectingUsers.has(user.id);
                                            return ((0, jsx_runtime_1.jsxs)(Table_1.TableRow, { children: [(0, jsx_runtime_1.jsx)(Table_1.TableCell, { className: "font-medium truncate", children: user.email }), (0, jsx_runtime_1.jsx)(Table_1.TableCell, { className: "truncate", children: user.name || empty }), (0, jsx_runtime_1.jsx)(Table_1.TableCell, { className: "truncate tabular-nums", children: showDate(user.createdAt) }), (0, jsx_runtime_1.jsx)(Table_1.TableCell, { children: (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-2", children: [(0, jsx_runtime_1.jsx)(Loading_1.LoadingButton, { type: "button", loading: approvingUsers.has(user.id), disabled: busy, onClick: () => handleApproveUser(user.id), className: "px-3 py-1.5 text-sm", children: approvingUsers.has(user.id) ? t('common.status.approving') : t('common.actions.approve') }), (0, jsx_runtime_1.jsx)(Loading_1.LoadingButton, { type: "button", loading: rejectingUsers.has(user.id), disabled: busy, onClick: () => handleRejectUser(user.id), className: "px-3 py-1.5 text-sm !bg-transparent border border-slate-300 dark:border-slate-600 !text-red-600 dark:!text-red-400 hover:!bg-red-50 dark:hover:!bg-red-900/20", children: rejectingUsers.has(user.id) ? t('common.status.rejecting') : t('common.actions.reject') })] }) })] }, user.id));
                                        }) })] })] }))] }), (0, jsx_runtime_1.jsx)(Modal_1.ConfirmModal, { isOpen: !!userToDeleteId, onClose: () => {
                    if (!isDeleting)
                        setUserToDeleteId(null);
                }, onConfirm: confirmDeleteUser, title: t('common.adminPanel.confirmDeleteUserTitle'), message: t('common.adminPanel.confirmDeleteUserMessage'), confirmText: t('common.actions.delete'), cancelText: t('common.actions.cancel'), type: "danger", isLoading: isDeleting, loadingText: t('common.status.deleting'), t: t })] }));
};
exports.UserManagementTable = UserManagementTable;
exports.UserManagementTable.displayName = 'UserManagementTable';
